import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, TrendingUp, Percent, Euro, Wine, Store, Users, Building2, Download, Info } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { usePageTitle } from '../hooks/usePageTitle';
import { jsPDF } from 'jspdf';

const ProfitCalculator = ({ hideChrome = false }) => {
  usePageTitle('Calcolatore Guadagni | IlTuoAmaro.it');

  const [calculatorType, setCalculatorType] = useState('mescita'); // 'mescita' or 'rivendita'
  const [includeVAT, setIncludeVAT] = useState(false);
  const VAT_RATE = 0.22; // 22% IVA italiana

  // Mescita state
  const [mescita, setMescita] = useState({
    bottleVolume: 700, // ml
    doseVolume: 40, // ml per bicchiere
    bottleCost: 25, // euro
    glassPrice: 8 // euro per bicchiere
  });

  // Rivendita state
  const [rivendita, setRivendita] = useState({
    productionCost: 20 // euro
  });

  // Calcoli Mescita
  const calculateMescita = () => {
    const numberOfGlasses = Math.floor(mescita.bottleVolume / mescita.doseVolume);
    const costPerGlass = mescita.bottleCost / numberOfGlasses;
    const totalRevenue = numberOfGlasses * mescita.glassPrice;
    const totalProfit = totalRevenue - mescita.bottleCost;
    const profitPerGlass = totalProfit / numberOfGlasses;
    const marginPercent = (totalProfit / totalRevenue) * 100;

    // Con IVA
    const totalRevenueWithVAT = includeVAT ? totalRevenue * (1 + VAT_RATE) : totalRevenue;
    const bottleCostWithVAT = includeVAT ? mescita.bottleCost * (1 + VAT_RATE) : mescita.bottleCost;
    const costPerGlassWithVAT = includeVAT ? costPerGlass * (1 + VAT_RATE) : costPerGlass;
    const totalProfitWithVAT = totalRevenueWithVAT - bottleCostWithVAT;

    return {
      numberOfGlasses,
      costPerGlass: includeVAT ? costPerGlassWithVAT : costPerGlass,
      totalRevenue: includeVAT ? totalRevenueWithVAT : totalRevenue,
      totalCost: includeVAT ? bottleCostWithVAT : mescita.bottleCost,
      totalProfit: includeVAT ? totalProfitWithVAT : totalProfit,
      profitPerGlass: includeVAT ? totalProfitWithVAT / numberOfGlasses : profitPerGlass,
      marginPercent
    };
  };

  // Calcoli Rivendita
  const calculateRivendita = () => {
    const baseCost = rivendita.productionCost;
    
    // Cliente finale: +100%
    const finalCustomerPrice = baseCost * 2;
    const finalCustomerProfit = finalCustomerPrice - baseCost;
    const finalCustomerMargin = 100;

    // Rivenditore: +60%
    const resellerPrice = baseCost * 1.6;
    const resellerProfit = resellerPrice - baseCost;
    const resellerMargin = 60;

    // Distributore: 30% sul prezzo finale
    const distributorPrice = finalCustomerPrice * 0.7; // Distributore paga 70% del prezzo finale
    const distributorProfit = distributorPrice - baseCost;
    const distributorMargin = ((distributorProfit / distributorPrice) * 100);

    // Con IVA
    const applyVAT = (price) => includeVAT ? price * (1 + VAT_RATE) : price;

    return {
      finalCustomer: {
        price: applyVAT(finalCustomerPrice),
        profit: applyVAT(finalCustomerProfit),
        margin: finalCustomerMargin
      },
      reseller: {
        price: applyVAT(resellerPrice),
        profit: applyVAT(resellerProfit),
        margin: resellerMargin
      },
      distributor: {
        price: applyVAT(distributorPrice),
        profit: applyVAT(distributorProfit),
        margin: distributorMargin
      }
    };
  };

  const mescitaResults = calculatorType === 'mescita' ? calculateMescita() : null;
  const rivenditaResults = calculatorType === 'rivendita' ? calculateRivendita() : null;

  // Export PDF functions
  const exportMescitaPDF = () => {
    const doc = new jsPDF();
    const results = calculateMescita();
    
    // Header
    doc.setFillColor(147, 51, 234); // Purple
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.text('IlTuoAmaro.it', 105, 20, { align: 'center' });
    doc.setFontSize(16);
    doc.text('Calcolatore Guadagni - Mescita', 105, 32, { align: 'center' });
    
    // Date
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Data: ${new Date().toLocaleDateString('it-IT')}`, 20, 50);
    doc.text(`IVA ${includeVAT ? 'Inclusa' : 'Esclusa'} (22%)`, 150, 50);
    
    // Input Data
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Dati Inseriti:', 20, 65);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);
    doc.text(`Volume Bottiglia: ${mescita.bottleVolume} ml`, 20, 75);
    doc.text(`ml per Bicchiere: ${mescita.doseVolume} ml`, 20, 82);
    doc.text(`Prezzo Acquisto: €${mescita.bottleCost.toFixed(2)}`, 20, 89);
    doc.text(`Prezzo al Bicchiere: €${mescita.glassPrice.toFixed(2)}`, 20, 96);
    
    // Results
    doc.setFillColor(147, 51, 234);
    doc.rect(20, 110, 170, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('RISULTATI', 105, 117, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);
    let y = 130;
    
    doc.setFont(undefined, 'bold');
    doc.text('Numero Bicchieri:', 20, y);
    doc.setFont(undefined, 'normal');
    doc.text(`${results.numberOfGlasses}`, 120, y);
    y += 10;
    
    doc.setFont(undefined, 'bold');
    doc.text('Costo per Bicchiere:', 20, y);
    doc.setFont(undefined, 'normal');
    doc.text(`€${results.costPerGlass.toFixed(2)}`, 120, y);
    y += 10;
    
    doc.setFont(undefined, 'bold');
    doc.text('Ricavo Totale:', 20, y);
    doc.setFont(undefined, 'normal');
    doc.text(`€${results.totalRevenue.toFixed(2)}`, 120, y);
    y += 10;
    
    doc.setFont(undefined, 'bold');
    doc.text('Prezzo Acquisto:', 20, y);
    doc.setFont(undefined, 'normal');
    doc.text(`€${results.totalCost.toFixed(2)}`, 120, y);
    y += 10;
    
    doc.setFont(undefined, 'bold');
    doc.setTextColor(34, 197, 94); // Green
    doc.text('Guadagno Totale:', 20, y);
    doc.setFont(undefined, 'normal');
    doc.text(`€${results.totalProfit.toFixed(2)}`, 120, y);
    y += 10;
    
    doc.setFont(undefined, 'bold');
    doc.text('Guadagno per Bicchiere:', 20, y);
    doc.setFont(undefined, 'normal');
    doc.text(`€${results.profitPerGlass.toFixed(2)}`, 120, y);
    y += 10;
    
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text('Margine:', 20, y);
    doc.setFont(undefined, 'normal');
    doc.text(`${results.marginPercent.toFixed(1)}%`, 120, y);
    
    // Footer
    doc.setFontSize(9);
    doc.setTextColor(128, 128, 128);
    doc.text('IlTuoAmaro.it - Calcolatore Guadagni', 105, 280, { align: 'center' });
    doc.text('I calcoli sono indicativi e possono variare in base a condizioni specifiche', 105, 285, { align: 'center' });
    
    doc.save(`IlTuoAmaro_Mescita_${new Date().toLocaleDateString('it-IT').replace(/\//g, '-')}.pdf`);
  };

  const exportRivenditaPDF = () => {
    const doc = new jsPDF();
    const results = calculateRivendita();
    
    // Header
    doc.setFillColor(147, 51, 234);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.text('IlTuoAmaro.it', 105, 20, { align: 'center' });
    doc.setFontSize(16);
    doc.text('Calcolatore Guadagni - Rivendita', 105, 32, { align: 'center' });
    
    // Date
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Data: ${new Date().toLocaleDateString('it-IT')}`, 20, 50);
    doc.text(`IVA ${includeVAT ? 'Inclusa' : 'Esclusa'} (22%)`, 150, 50);
    
    // Input Data
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Dati Inseriti:', 20, 65);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);
    doc.text(`Prezzo di Acquisto Liquore: €${rivendita.productionCost.toFixed(2)}`, 20, 75);
    
    let y = 95;
    
    // Cliente Finale
    doc.setFillColor(34, 197, 94); // Green
    doc.rect(20, y, 170, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('CLIENTE FINALE (+100%)', 105, y + 7, { align: 'center' });
    y += 15;
    
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);
    doc.text('Prezzo Vendita:', 20, y);
    doc.text(`€${results.finalCustomer.price.toFixed(2)}`, 120, y);
    y += 7;
    doc.text('Guadagno:', 20, y);
    doc.text(`€${results.finalCustomer.profit.toFixed(2)}`, 120, y);
    y += 7;
    doc.text('Margine:', 20, y);
    doc.text(`${results.finalCustomer.margin}%`, 120, y);
    y += 15;
    
    // Rivenditore
    doc.setFillColor(37, 99, 235); // Blue
    doc.rect(20, y, 170, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('RIVENDITORE (+60%)', 105, y + 7, { align: 'center' });
    y += 15;
    
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);
    doc.text('Prezzo Vendita:', 20, y);
    doc.text(`€${results.reseller.price.toFixed(2)}`, 120, y);
    y += 7;
    doc.text('Guadagno:', 20, y);
    doc.text(`€${results.reseller.profit.toFixed(2)}`, 120, y);
    y += 7;
    doc.text('Margine:', 20, y);
    doc.text(`${results.reseller.margin}%`, 120, y);
    y += 15;
    
    // Distributore
    doc.setFillColor(147, 51, 234); // Purple
    doc.rect(20, y, 170, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('DISTRIBUTORE (70% prezzo finale)', 105, y + 7, { align: 'center' });
    y += 15;
    
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);
    doc.text('Prezzo Vendita:', 20, y);
    doc.text(`€${results.distributor.price.toFixed(2)}`, 120, y);
    y += 7;
    doc.text('Guadagno:', 20, y);
    doc.text(`€${results.distributor.profit.toFixed(2)}`, 120, y);
    y += 7;
    doc.text('Margine:', 20, y);
    doc.text(`${results.distributor.margin.toFixed(1)}%`, 120, y);
    
    // Footer
    doc.setFontSize(9);
    doc.setTextColor(128, 128, 128);
    doc.text('IlTuoAmaro.it - Calcolatore Guadagni', 105, 280, { align: 'center' });
    doc.text('I calcoli sono indicativi e possono variare in base a condizioni specifiche', 105, 285, { align: 'center' });
    
    doc.save(`IlTuoAmaro_Rivendita_${new Date().toLocaleDateString('it-IT').replace(/\//g, '-')}.pdf`);
  };

  return (
    <div className={hideChrome ? 'flex flex-col' : 'min-h-screen bg-gradient-to-b from-purple-50 to-white flex flex-col'}>
      {!hideChrome && <Header />}
      
      <main className={hideChrome ? 'flex-grow py-2' : 'flex-grow container mx-auto px-4 py-12'}>
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <Calculator className="h-8 w-8 text-purple-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Calcolatore Guadagni
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Calcola i tuoi potenziali guadagni per mescita o rivendita
          </p>
        </div>

        {/* Type Selector */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setCalculatorType('mescita')}
                className={`flex-1 py-4 px-6 rounded-lg border-2 transition-all ${
                  calculatorType === 'mescita'
                    ? 'border-purple-600 bg-purple-50 text-purple-700'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <Wine className="h-6 w-6 mx-auto mb-2" />
                <div className="font-semibold">Mescita</div>
                <div className="text-sm text-gray-600">Bar, Pub, Ristoranti</div>
              </button>

              <button
                onClick={() => setCalculatorType('rivendita')}
                className={`flex-1 py-4 px-6 rounded-lg border-2 transition-all ${
                  calculatorType === 'rivendita'
                    ? 'border-purple-600 bg-purple-50 text-purple-700'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <Store className="h-6 w-6 mx-auto mb-2" />
                <div className="font-semibold">Rivendita</div>
                <div className="text-sm text-gray-600">Canali di Vendita</div>
              </button>
            </div>

            {/* IVA Toggle */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="flex items-center justify-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeVAT}
                  onChange={(e) => setIncludeVAT(e.target.checked)}
                  className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-gray-700 font-medium">
                  Includi IVA (22%)
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Mescita Calculator */}
        {calculatorType === 'mescita' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Wine className="h-6 w-6 mr-2 text-purple-600" />
                Calcolo Mescita (HoReCa)
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    Volume Bottiglia (ml)
                    <span className="ml-1 text-purple-500" title="Capacità totale della bottiglia">
                      <Info className="w-4 h-4" />
                    </span>
                  </label>
                  <input
                    type="number"
                    value={mescita.bottleVolume}
                    onChange={(e) => setMescita({...mescita, bottleVolume: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="700"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Capacità totale della bottiglia in millilitri (es: 700ml, 1000ml)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    ml per Dose/Bicchiere
                    <span className="ml-1 text-purple-500" title="Quantità servita per ogni consumazione">
                      <Info className="w-4 h-4" />
                    </span>
                  </label>
                  <input
                    type="number"
                    value={mescita.doseVolume}
                    onChange={(e) => setMescita({...mescita, doseVolume: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="40"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Quantità servita per ogni bicchiere (standard: 40ml per amaro)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    Prezzo di Acquisto Liquore (€)
                    <span className="ml-1 text-purple-500" title="Costo di acquisto dal fornitore">
                      <Info className="w-4 h-4" />
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={mescita.bottleCost}
                    onChange={(e) => setMescita({...mescita, bottleCost: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="25.00"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Quanto paghi per acquistare una bottiglia dal produttore.{' '}
                    <Link to="/builder" className="text-purple-600 hover:text-purple-700 font-medium underline">
                      Crea il tuo liquore qui
                    </Link>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    Prezzo al Bicchiere (€)
                    <span className="ml-1 text-purple-500" title="Prezzo di vendita al cliente finale">
                      <Info className="w-4 h-4" />
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={mescita.glassPrice}
                    onChange={(e) => setMescita({...mescita, glassPrice: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="8.00"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Quanto vendi ogni bicchiere ai tuoi clienti
                  </p>
                </div>
              </div>

              {/* Info Box Mescita */}
              <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 text-sm mb-2">ℹ️ Esempio Pratico</h4>
                <p className="text-xs text-purple-800 leading-relaxed">
                  Con una bottiglia da <strong>700ml</strong>, servendo <strong>40ml</strong> a bicchiere, ottieni circa <strong>17 bicchieri</strong>. 
                  Se acquisti la bottiglia a <strong>€25</strong> e vendi ogni bicchiere a <strong>€8</strong>, il tuo guadagno sarà di circa <strong>€111</strong> (margine 82%).
                </p>
              </div>
            </div>

            {/* Results */}
            {mescitaResults && (
              <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl shadow-lg p-8 text-white">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold flex items-center">
                    <TrendingUp className="h-6 w-6 mr-2" />
                    Risultati Mescita
                  </h3>
                  <button
                    onClick={exportMescitaPDF}
                    className="flex items-center space-x-2 px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-semibold"
                  >
                    <Download className="h-4 w-4" />
                    <span>Esporta PDF</span>
                  </button>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                    <div className="text-purple-200 text-sm mb-2">Numero Bicchieri</div>
                    <div className="text-3xl font-bold">{mescitaResults.numberOfGlasses}</div>
                  </div>

                  <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                    <div className="text-purple-200 text-sm mb-2">Costo per Bicchiere</div>
                    <div className="text-3xl font-bold text-yellow-300">€{mescitaResults.costPerGlass.toFixed(2)}</div>
                  </div>

                  <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                    <div className="text-purple-200 text-sm mb-2">Ricavo Totale</div>
                    <div className="text-3xl font-bold">€{mescitaResults.totalRevenue.toFixed(2)}</div>
                  </div>

                  <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                    <div className="text-purple-200 text-sm mb-2">Prezzo Acquisto</div>
                    <div className="text-3xl font-bold">€{mescitaResults.totalCost.toFixed(2)}</div>
                  </div>

                  <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                    <div className="text-purple-200 text-sm mb-2">Guadagno Totale</div>
                    <div className="text-3xl font-bold text-green-300">€{mescitaResults.totalProfit.toFixed(2)}</div>
                  </div>

                  <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                    <div className="text-purple-200 text-sm mb-2">Guadagno per Bicchiere</div>
                    <div className="text-3xl font-bold text-green-300">€{mescitaResults.profitPerGlass.toFixed(2)}</div>
                  </div>

                  <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm col-span-full md:col-span-2 lg:col-span-1 mx-auto lg:mx-0">
                    <div className="text-purple-200 text-sm mb-2">Margine %</div>
                    <div className="text-3xl font-bold flex items-center justify-center lg:justify-start">
                      {mescitaResults.marginPercent.toFixed(1)}%
                      <Percent className="h-6 w-6 ml-2" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Rivendita Calculator */}
        {calculatorType === 'rivendita' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Store className="h-6 w-6 mr-2 text-purple-600" />
                Calcolo Rivendita
              </h2>

              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  Prezzo di Acquisto Liquore (€)
                  <span className="ml-1 text-purple-500" title="Costo base del prodotto">
                    <Info className="w-4 h-4" />
                  </span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={rivendita.productionCost}
                  onChange={(e) => setRivendita({...rivendita, productionCost: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="20.00"
                />
                <p className="mt-2 text-sm text-gray-500">
                  💡 Inserisci il prezzo a cui acquisti il liquore dal produttore.{' '}
                  <Link to="/builder" className="text-purple-600 hover:text-purple-700 font-medium underline">
                    Crea il tuo liquore personalizzato
                  </Link>
                </p>
                <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-xs text-purple-800">
                    <span className="font-semibold">Suggerimento:</span> Il calcolatore mostrerà automaticamente i prezzi di vendita suggeriti per tre diversi canali di distribuzione con i rispettivi margini di guadagno.
                  </p>
                </div>
              </div>
            </div>

            {/* Results */}
            {rivenditaResults && (
              <div className="space-y-6">
                {/* Export Button */}
                <div className="flex justify-end">
                  <button
                    onClick={exportRivenditaPDF}
                    className="flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold shadow-lg"
                  >
                    <Download className="h-5 w-5" />
                    <span>Esporta PDF</span>
                  </button>
                </div>

                {/* Cliente Finale */}
                <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-xl shadow-lg p-8 text-white">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold flex items-center">
                      <Users className="h-6 w-6 mr-2" />
                      Cliente Finale
                    </h3>
                    <span className="px-4 py-2 bg-white/20 rounded-lg text-sm font-semibold">
                      +{rivenditaResults.finalCustomer.margin}%
                    </span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                      <div className="text-green-200 text-sm mb-2">Prezzo Vendita</div>
                      <div className="text-3xl font-bold">€{rivenditaResults.finalCustomer.price.toFixed(2)}</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                      <div className="text-green-200 text-sm mb-2">Guadagno</div>
                      <div className="text-3xl font-bold">€{rivenditaResults.finalCustomer.profit.toFixed(2)}</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                      <div className="text-green-200 text-sm mb-2">Margine</div>
                      <div className="text-3xl font-bold">{rivenditaResults.finalCustomer.margin}%</div>
                    </div>
                  </div>
                </div>

                {/* Rivenditore */}
                <div className="bg-gradient-to-br from-blue-600 to-cyan-700 rounded-xl shadow-lg p-8 text-white">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold flex items-center">
                      <Store className="h-6 w-6 mr-2" />
                      Rivenditore
                    </h3>
                    <span className="px-4 py-2 bg-white/20 rounded-lg text-sm font-semibold">
                      +{rivenditaResults.reseller.margin}%
                    </span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                      <div className="text-blue-200 text-sm mb-2">Prezzo Vendita</div>
                      <div className="text-3xl font-bold">€{rivenditaResults.reseller.price.toFixed(2)}</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                      <div className="text-blue-200 text-sm mb-2">Guadagno</div>
                      <div className="text-3xl font-bold">€{rivenditaResults.reseller.profit.toFixed(2)}</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                      <div className="text-blue-200 text-sm mb-2">Margine</div>
                      <div className="text-3xl font-bold">{rivenditaResults.reseller.margin}%</div>
                    </div>
                  </div>
                </div>

                {/* Distributore */}
                <div className="bg-gradient-to-br from-purple-600 to-pink-700 rounded-xl shadow-lg p-8 text-white">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold flex items-center">
                      <Building2 className="h-6 w-6 mr-2" />
                      Distributore
                    </h3>
                    <span className="px-4 py-2 bg-white/20 rounded-lg text-sm font-semibold">
                      {rivenditaResults.distributor.margin.toFixed(1)}%
                    </span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                      <div className="text-purple-200 text-sm mb-2">Prezzo Vendita</div>
                      <div className="text-3xl font-bold">€{rivenditaResults.distributor.price.toFixed(2)}</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                      <div className="text-purple-200 text-sm mb-2">Guadagno</div>
                      <div className="text-3xl font-bold">€{rivenditaResults.distributor.profit.toFixed(2)}</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                      <div className="text-purple-200 text-sm mb-2">Margine</div>
                      <div className="text-3xl font-bold">{rivenditaResults.distributor.margin.toFixed(1)}%</div>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-purple-200">
                    * Il distributore paga il 70% del prezzo al cliente finale
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-2xl shadow-2xl">
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
            
            <div className="relative px-8 py-12 md:px-16 md:py-16 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                <Wine className="h-8 w-8 text-white" />
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                CREA ORA IL TUO LIQUORE
              </h2>
              
              <p className="text-xl md:text-2xl text-white/90 mb-8 font-medium">
                Inizia a guadagnare subito
              </p>
              
              <p className="text-white/80 mb-8 max-w-2xl mx-auto text-lg">
                Crea il tuo liquore personalizzato con ingredienti premium e inizia il tuo business oggi stesso.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="/builder"
                  className="group relative px-8 py-4 bg-white text-purple-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2"
                >
                  <span>Crea il Tuo Liquore</span>
                  <TrendingUp className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
                
                <a
                  href="/contatti"
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-xl font-semibold text-lg hover:bg-white/20 transition-all duration-300"
                >
                  Richiedi Consulenza
                </a>
              </div>
              
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-white/80 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Ingredienti Premium</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Produzione Artigianale</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Margini Elevati</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
              <Euro className="h-5 w-5 mr-2" />
              Note sui Calcoli
            </h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• I calcoli sono indicativi e possono variare in base a condizioni specifiche</li>
              <li>• L'IVA applicata è al 22% (aliquota standard italiana per bevande alcoliche)</li>
              <li>• Per la mescita, considerare anche eventuali costi di servizio e ghiaccio</li>
              <li>• Per la rivendita, i margini possono essere negoziati in base ai volumi</li>
            </ul>
          </div>
        </div>
      </main>

      {!hideChrome && <Footer />}
    </div>
  );
};

export default ProfitCalculator;
